import { useMemo, useState } from "react";
import { Eye, ShieldCheck, Users } from "lucide-react";

import { Role } from "@/services/api/RoleService";
import {
  AbrangenciaMap,
  AbrangenciaNivelEnum,
  PermissaoEnum,
  PermissaoLabelsMap,
  RoleLabelsMap,
  RoleFiltrosCriteria,
  RoleFiltrosOptions,
} from "@workflow/types";

import { Column, DataTable } from "@/components/table/DataTable";
import { ActionMenu } from "@/components/shared/ActionMenu";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { rolesQueries, rolesKeys } from "@/services/api/queries/roles.queries";
import {
  getPermissaoModulo,
  PERMISSAO_MODULO_ORDER,
  PERMISSAO_MODULO_STYLE,
} from "@/lib/permissao-modulo";
import { cn } from "@/lib/utils";

import { ViewRolePermissionsDialog } from "./ViewRolePermissionsDialog";

const permissaoLabel = (nome: string) => PermissaoLabelsMap[nome as PermissaoEnum]?.nome || nome;

const moduloIndex = (nome: string) => PERMISSAO_MODULO_ORDER.indexOf(getPermissaoModulo(nome));

// Quantos chips de permissão mostrar inline antes do "ver todas".
const PREVIEW_COUNT = 3;

/** Chip de uma permissão, colorido pelo módulo. */
const PermissionChip = ({ nome }: { nome: string }) => {
  const style = PERMISSAO_MODULO_STYLE[getPermissaoModulo(nome)];
  return (
    <span
      className={cn(
        "inline-flex max-w-[160px] items-center gap-1.5 rounded-full border py-0.5 pl-1.5 pr-2 text-[11px] font-medium",
        style.chip,
      )}
      title={PermissaoLabelsMap[nome as PermissaoEnum]?.descricao || nome}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", style.dot)} />
      <span className="truncate">{permissaoLabel(nome)}</span>
    </span>
  );
};

/**
 * Prévia das permissões: mostra alguns chips reais (com o nome, coloridos por
 * módulo) + um botão "+N · ver todas" que abre o dialog. Assim dá pra ter uma
 * amostra na hora sem poluir, e o resto fica explícito a um clique.
 */
const PermissionPreview = ({
  permissions,
  onVerTodas,
}: {
  permissions: Role["permissoes"];
  onVerTodas: () => void;
}) => {
  const total = permissions?.length ?? 0;
  if (total === 0) {
    return (
      <span className="text-[11px] italic text-muted-foreground">Nenhuma permissão definida</span>
    );
  }

  // Amostra estável: por ordem de módulo (cores variadas) e depois por rótulo.
  const sorted = [...permissions].sort(
    (a, b) =>
      moduloIndex(a.nome) - moduloIndex(b.nome) ||
      permissaoLabel(a.nome).localeCompare(permissaoLabel(b.nome)),
  );
  const visible = sorted.slice(0, PREVIEW_COUNT);
  const rest = total - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((p) => (
        <PermissionChip key={p.nome} nome={p.nome} />
      ))}

      {rest > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onVerTodas();
          }}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-solid hover:bg-muted hover:text-foreground"
          title="Abrir para ver todas as permissões"
        >
          +{rest} · ver todas
        </button>
      )}
    </div>
  );
};

const CountPill = ({ count }: { count: number }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-xs font-medium",
      count > 0
        ? "border-border bg-muted/50 text-foreground"
        : "border-border/60 bg-transparent text-muted-foreground",
    )}
  >
    <Users className="h-3 w-3 opacity-60" />
    {count}
  </span>
);

// --- Eixos de acesso (abrangência = linhas; sensibilidade = campos) ---

// Cor por nível: ALTA = mais acesso (verde) → BAIXA = mais restrito (âmbar).
const NIVEL_STYLE: Record<AbrangenciaNivelEnum, string> = {
  [AbrangenciaNivelEnum.ALTA]:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  [AbrangenciaNivelEnum.MEDIA]:
    "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  [AbrangenciaNivelEnum.BAIXA]:
    "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

// O que cada nível significa em cada eixo (a explicação é o próprio conteúdo, não tooltip).
const NIVEL_DESCRICAO: Record<
  "abrangencia" | "sensibilidade",
  Record<AbrangenciaNivelEnum, string>
> = {
  abrangencia: {
    [AbrangenciaNivelEnum.ALTA]: "Vê todos os registros (jobs, planilhas, faturamentos).",
    [AbrangenciaNivelEnum.MEDIA]:
      "Vê registros da equipe, próprios e do squad na janela retroativa.",
    [AbrangenciaNivelEnum.BAIXA]: "Vê apenas registros da própria equipe ou criados por si.",
  },
  sensibilidade: {
    [AbrangenciaNivelEnum.ALTA]:
      "Vê todos os campos financeiros (valores, receita, rentabilidade).",
    [AbrangenciaNivelEnum.MEDIA]:
      "Vê parte dos campos financeiros; os mais sensíveis ficam ocultos.",
    [AbrangenciaNivelEnum.BAIXA]: "Campos financeiros sensíveis ficam mascarados.",
  },
};

const NivelBadge = ({ nivel }: { nivel?: AbrangenciaNivelEnum }) => {
  if (!nivel) return <span className="text-[11px] italic text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        NIVEL_STYLE[nivel],
      )}
    >
      {AbrangenciaMap[nivel]}
    </span>
  );
};

// --- Legenda de cores por módulo ---
export const ModuloLegend = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border bg-card px-3 py-2 shadow-sm">
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      Módulos
    </span>
    {PERMISSAO_MODULO_ORDER.map((m) => {
      const style = PERMISSAO_MODULO_STYLE[m];
      return (
        <span
          key={m}
          className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
        >
          <span className={cn("h-2 w-2 rounded-full", style.dot)} />
          {style.label}
        </span>
      );
    })}
  </div>
);

type RoleTableVariant = "permissoes" | "abrangencia" | "sensibilidade";

const idColumn: Column<Role> = {
  key: "role.id",
  title: "ID",
  sortable: true,
  width: "min-w-[80px]",
  render: (role) => <span className="font-mono text-xs text-muted-foreground">{role.id}</span>,
  raw: (role) => role.id.toString(),
};

const perfilColumn: Column<Role> = {
  key: "role.nome",
  title: "Perfil / Função",
  sortable: true,
  width: "min-w-[260px]",
  render: (role) => (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <ShieldCheck className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-foreground">
          {RoleLabelsMap[role.nome as keyof typeof RoleLabelsMap] || role.nome}
        </p>
        <code className="font-mono text-[10px] text-muted-foreground/60">{role.nome}</code>
      </div>
    </div>
  ),
  raw: (role) => `${RoleLabelsMap[role.nome as keyof typeof RoleLabelsMap] || ""} ${role.nome}`,
};

const usuariosColumn: Column<Role> = {
  key: "totalPessoasAtivas",
  title: "Usuários",
  width: "min-w-[110px]",
  render: (role) => <CountPill count={role.totalPessoasAtivas ?? 0} />,
  raw: (role) => String(role.totalPessoasAtivas ?? 0),
};

/** Coluna nível + descrição para os eixos de acesso (abrangência/sensibilidade). */
const nivelColumns = (eixo: "abrangencia" | "sensibilidade"): Column<Role>[] => {
  const nivelOf = (role: Role) =>
    eixo === "abrangencia" ? role.abrangenciaNivel : role.sensibilidadeNivel;
  return [
    {
      key: `${eixo}.nivel`,
      title: "Nível",
      width: "min-w-[110px]",
      render: (role) => <NivelBadge nivel={nivelOf(role)} />,
      raw: (role) => (nivelOf(role) ? AbrangenciaMap[nivelOf(role)!] : ""),
    },
    {
      key: `${eixo}.descricao`,
      title: eixo === "abrangencia" ? "O que enxerga (registros)" : "O que enxerga (campos)",
      width: "min-w-[360px]",
      render: (role) => {
        const nivel = nivelOf(role);
        // Na sensibilidade, sinaliza quando diverge da abrangência (ex.: freela).
        const diverge =
          eixo === "sensibilidade" && nivel != null && nivel !== role.abrangenciaNivel;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              {nivel ? NIVEL_DESCRICAO[eixo][nivel] : "—"}
            </span>
            {diverge && (
              <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">
                Diverge da abrangência ({AbrangenciaMap[role.abrangenciaNivel!]})
              </span>
            )}
          </div>
        );
      },
      raw: (role) => {
        const nivel = nivelOf(role);
        return nivel ? NIVEL_DESCRICAO[eixo][nivel] : "";
      },
    },
  ];
};

const VARIANT_CONFIG: Record<RoleTableVariant, { storageKey: string; exportFilename: string }> = {
  permissoes: { storageKey: "roles-listagem-v1", exportFilename: "funcoes" },
  abrangencia: { storageKey: "roles-abrangencia-v1", exportFilename: "funcoes-abrangencia" },
  sensibilidade: { storageKey: "roles-sensibilidade-v1", exportFilename: "funcoes-sensibilidade" },
};

export function RoleListTable({ variant = "permissoes" }: { variant?: RoleTableVariant } = {}) {
  const { tableProps, isError, invalidate } = useServerDataTable<
    Role,
    RoleFiltrosOptions,
    RoleFiltrosCriteria
  >({
    queryOptions: rolesQueries.list,
    filters: rolesQueries.filtros,
    invalidateKey: rolesKeys.list,
  });

  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const openView = (role: Role) => {
    setViewingRole(role);
    setIsViewOpen(true);
  };

  const columns = useMemo<Column<Role>[]>(() => {
    if (variant === "permissoes") {
      return [
        {
          type: "headless",
          key: "actions",
          title: "Ações",
          width: "min-w-[40px]",
          render: (role) => (
            <ActionMenu
              items={[
                {
                  label: "Ver permissões e usuários",
                  icon: <Eye className="h-4 w-4" />,
                  onClick: () => openView(role),
                },
              ]}
            />
          ),
        },
        idColumn,
        perfilColumn,
        {
          key: "permissoes",
          title: "Permissões de Acesso",
          width: "min-w-[300px]",
          render: (role) => (
            <PermissionPreview permissions={role.permissoes} onVerTodas={() => openView(role)} />
          ),
          raw: (role) => role.permissoes?.map((p) => permissaoLabel(p.nome)).join(", ") || "",
        },
        usuariosColumn,
      ];
    }
    return [idColumn, perfilColumn, ...nivelColumns(variant), usuariosColumn];
  }, [variant]);

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={VARIANT_CONFIG[variant].storageKey}
        columns={columns}
        defaultSortColumn="role.nome"
        defaultSortOrder="ASC"
        searchPlaceholder="Pesquisar por função..."
        emptyMessage={
          isError
            ? "Não foi possível carregar as funções. Tente recarregar."
            : "Nenhuma função encontrada."
        }
        isError={isError}
        onRetry={invalidate}
        exportFilename={VARIANT_CONFIG[variant].exportFilename}
        onRowClick={variant === "permissoes" ? openView : undefined}
        className="border-none"
      />

      {variant === "permissoes" && (
        <ViewRolePermissionsDialog
          open={isViewOpen}
          onOpenChange={setIsViewOpen}
          role={viewingRole}
        />
      )}
    </>
  );
}
